import { memo, type ReactElement } from 'react';

import { WithChildren, WithError } from '@eva/types/dto';

import type {
  RearrangeHandler,
  GroupedTableClassNames,
  ControlDescriptor,
  RowState,
  GetFormFunction,
  GetPlaceholderFunction,
} from './types';

import Item from './Item';

export interface PlainListProps<I, G> {
  items: I[];
  groups: G[];
  groupId: number;
  dndEnabled?: boolean;
  dragInProcess?: boolean;
  selectedItem?: I;
  classNames: GroupedTableClassNames;
  itemStates: { [key: number]: RowState };
  itemControls?: ControlDescriptor<I, G>[];
  getItemId: (item: I) => number;
  getItemForm: GetFormFunction<I, RowState>;
  getItemLabel: (item: I) => ReactElement | string;
  getItemError?: (error: string, item: I) => ReactElement | string;
  getItemPlaceholder?: GetPlaceholderFunction<I, RowState>;
  isItemSortable: (item: I) => boolean;
  onRearrange: RearrangeHandler;
  updateItemState: (item: I, updates: Partial<RowState>) => void;
}

const PlainList = <C, G extends WithChildren<C> & WithError>({
  items,
  groups,
  groupId,
  dndEnabled,
  classNames,
  itemStates,
  itemControls,
  selectedItem,
  dragInProcess,
  getItemId,
  getItemForm,
  getItemLabel,
  getItemError,
  isItemSortable,
  getItemPlaceholder,
  onRearrange,
  updateItemState,
}: PlainListProps<C, G>) => {
  if (!items?.length && !dragInProcess) {
    return (
      <>
        {typeof getItemPlaceholder === 'function'
          ? getItemPlaceholder(groupId, itemStates, updateItemState, classNames)
          : null}
      </>
    );
  }

  return (
    <>
      {items?.map((item, index) => {
        const itemId = getItemId(item);
        const isSelected = getItemId(selectedItem) === itemId;
        const itemState = itemStates[itemId] || {};
        return (
          <Item
            item={item}
            key={itemId}
            groups={groups}
            groupId={groupId}
            itemOrder={index}
            dndEnabled={dndEnabled}
            isSelected={isSelected}
            controls={itemControls}
            classNames={classNames}
            itemState={itemState}
            getItemId={getItemId}
            getItemForm={getItemForm}
            getItemLabel={getItemLabel}
            getItemError={getItemError}
            isItemSortable={isItemSortable}
            onRearrange={onRearrange}
            updateItemState={updateItemState}
          />
        );
      })}
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(PlainList);
